import * as React from "react";
import { Filter } from "lucide-react";
import { cn } from "./lib/utils";
import { Button, type ButtonProps } from "./button";

export interface FilterButtonProps extends Omit<ButtonProps, 'variant'> {
  /** Number of active filters */
  activeCount?: number;
  /** Show count badge */
  showBadge?: boolean;
  /** Button variant */
  variant?: ButtonProps['variant'];
}

/**
 * FilterButton - Button to open filter panel with active count badge
 *
 * Features:
 * - Filter icon
 * - Active count badge (blue pill)
 * - Enhanced hover states
 * - Accessible
 */
const FilterButton = React.forwardRef<HTMLButtonElement, FilterButtonProps>(
  ({ activeCount = 0, showBadge = true, children, className, ...props }, ref) => {
    const hasActiveFilters = activeCount > 0;

    return (
      <Button
        ref={ref}
        variant="outline"
        className={cn(
          "relative border-2 border-gray-300 px-4 py-2.5 h-10",
          "hover:bg-gray-50 hover:border-gray-400",
          "transition-all",
          className
        )}
        {...props}
      >
        <Filter className="h-4 w-4 mr-2" />
        {children || "Filters"}
        {showBadge && hasActiveFilters && (
          <span className="ml-2 inline-flex items-center justify-center rounded-full bg-blue-500 px-2 py-0.5 text-xs font-medium text-white">
            {activeCount}
          </span>
        )}
      </Button>
    );
  }
);
FilterButton.displayName = "FilterButton";

export { FilterButton };
